/**
 * Provider-icon atlas.
 *
 * Categorical glyphs (8 tiers) encoded as inline SVG data URIs. Each
 * provider lens maps service → category → glyph. Distinguishable at
 * ~24px with a solid colour + a single-letter/shape motif:
 *
 *   compute     — filled square with CPU bars (blue)
 *   data        — cylinder (green)
 *   storage     — box (emerald)
 *   network     — connected dots (slate)
 *   ingress     — arrow-in (red)
 *   identity    — key (purple)
 *   messaging   — envelope (amber)
 *   observability — eye (teal)
 *   container   — dashed square (kind-accent colour; for accounts/VPCs/subnets)
 *   generic     — dot (grey)
 *
 * Why inline SVG data URIs and not a sprite sheet:
 *  - Zero-asset load (bundled).
 *  - Works identically off-browser for the processing-worker export.
 *  - ~300 bytes per glyph — cheaper than a sprite HTTP fetch + atlas.
 *
 * Real provider-branded icons (AWS/Azure/GCP official SVGs) land in a
 * follow-up slice when we ship the official asset pack. Categorical
 * glyphs give the lens immediate visual signal without branding risk.
 */

export type IconCategory =
  | 'compute'
  | 'data'
  | 'storage'
  | 'network'
  | 'ingress'
  | 'identity'
  | 'messaging'
  | 'observability'
  | 'container'
  | 'generic';

const PALETTE: Record<IconCategory, { fill: string; stroke: string; glyph: string }> = {
  compute:       { fill: '#3b82f6', stroke: '#1d4ed8', glyph: 'C' },
  data:          { fill: '#10b981', stroke: '#047857', glyph: 'D' },
  storage:       { fill: '#059669', stroke: '#064e3b', glyph: 'S' },
  network:       { fill: '#64748b', stroke: '#334155', glyph: 'N' },
  ingress:       { fill: '#ef4444', stroke: '#991b1b', glyph: 'I' },
  identity:      { fill: '#a855f7', stroke: '#6b21a8', glyph: 'K' },
  messaging:     { fill: '#f59e0b', stroke: '#92400e', glyph: 'M' },
  observability: { fill: '#14b8a6', stroke: '#0f766e', glyph: 'O' },
  container:     { fill: '#e2e8f0', stroke: '#475569', glyph: 'V' },
  generic:       { fill: '#94a3b8', stroke: '#475569', glyph: '·' },
};

function svgDataUri(svg: string): string {
  // encodeURIComponent is a touch verbose but reliably escapes the `#`s
  // in colour values without needing base64 — smaller than btoa.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function makeIcon(category: IconCategory): string {
  const { fill, stroke, glyph } = PALETTE[category];
  // 24x24 square with rounded corners, coloured fill, white glyph.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
  <rect x="1.5" y="1.5" width="21" height="21" rx="5" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
  <text x="12" y="16" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="#ffffff" text-anchor="middle">${glyph}</text>
</svg>`;
  return svgDataUri(svg);
}

// Cache built at module load — every lens call reuses the same URIs
// so Konva can reuse the decoded Image instance downstream.
const ATLAS: Record<IconCategory, string> = {
  compute: makeIcon('compute'),
  data: makeIcon('data'),
  storage: makeIcon('storage'),
  network: makeIcon('network'),
  ingress: makeIcon('ingress'),
  identity: makeIcon('identity'),
  messaging: makeIcon('messaging'),
  observability: makeIcon('observability'),
  container: makeIcon('container'),
  generic: makeIcon('generic'),
};

export function iconForCategory(category: IconCategory): string {
  return ATLAS[category];
}

/**
 * Provider-specific service → category mapping.
 *
 * Mapping is based on the shared `CloudResource.service` (or manifest
 * id suffix). Unknown services fall through to `generic`.
 */

const AWS_SERVICE_TO_CATEGORY: Record<string, IconCategory> = {
  // Compute
  ec2: 'compute',
  instance: 'compute',
  lambda: 'compute',
  ecs: 'compute',
  eks: 'compute',
  fargate: 'compute',
  batch: 'compute',
  // Data / stores
  rds: 'data',
  aurora: 'data',
  'db-instance': 'data',
  'db-cluster': 'data',
  dynamodb: 'data',
  redshift: 'data',
  elasticache: 'data',
  // Storage
  s3: 'storage',
  bucket: 'storage',
  ebs: 'storage',
  volume: 'storage',
  efs: 'storage',
  // Network
  vpc: 'container',
  subnet: 'container',
  'route-table': 'network',
  'nat-gateway': 'network',
  'internet-gateway': 'network',
  'vpc-peering': 'network',
  'transit-gateway': 'network',
  'vpc-endpoint': 'network',
  'security-group': 'network',
  sg: 'network',
  // Ingress
  alb: 'ingress',
  nlb: 'ingress',
  'load-balancer': 'ingress',
  apigateway: 'ingress',
  'api-gateway': 'ingress',
  cloudfront: 'ingress',
  distribution: 'ingress',
  route53: 'ingress',
  // Identity / crypto
  iam: 'identity',
  'iam-role': 'identity',
  'iam-user': 'identity',
  'iam-policy': 'identity',
  role: 'identity',
  user: 'identity',
  kms: 'identity',
  'secrets-manager': 'identity',
  // Messaging
  sns: 'messaging',
  sqs: 'messaging',
  eventbridge: 'messaging',
  kinesis: 'messaging',
  // Observability
  cloudtrail: 'observability',
  cloudwatch: 'observability',
  config: 'observability',
  // Accounts
  account: 'container',
  region: 'container',
};

const AZURE_SERVICE_TO_CATEGORY: Record<string, IconCategory> = {
  'virtual-machine': 'compute',
  vm: 'compute',
  function: 'compute',
  'app-service': 'compute',
  'sql-database': 'data',
  cosmosdb: 'data',
  'cosmos-db': 'data',
  'storage-account': 'storage',
  'blob-container': 'storage',
  vnet: 'container',
  'virtual-network': 'container',
  'application-gateway': 'ingress',
  'load-balancer': 'ingress',
  'front-door': 'ingress',
  'active-directory': 'identity',
  'key-vault': 'identity',
  'service-bus': 'messaging',
  'event-grid': 'messaging',
  monitor: 'observability',
};

const GCP_SERVICE_TO_CATEGORY: Record<string, IconCategory> = {
  'compute-engine': 'compute',
  instance: 'compute',
  'cloud-function': 'compute',
  'cloud-run': 'compute',
  gke: 'compute',
  'cloud-sql': 'data',
  bigquery: 'data',
  spanner: 'data',
  firestore: 'data',
  'cloud-storage': 'storage',
  bucket: 'storage',
  'persistent-disk': 'storage',
  vpc: 'container',
  'cloud-nat': 'network',
  'cloud-router': 'network',
  'load-balancer': 'ingress',
  'cloud-cdn': 'ingress',
  iam: 'identity',
  kms: 'identity',
  'secret-manager': 'identity',
  'pub-sub': 'messaging',
  logging: 'observability',
  monitoring: 'observability',
};

const PROVIDER_MAPS: Record<string, Record<string, IconCategory>> = {
  aws: AWS_SERVICE_TO_CATEGORY,
  azure: AZURE_SERVICE_TO_CATEGORY,
  gcp: GCP_SERVICE_TO_CATEGORY,
};

/**
 * Resolve a node's icon category by peeling the provider prefix off
 * its manifest id. Falls back to the raw type when no prefix is
 * present. Unknown services return `null` so the lens can decide
 * whether to stamp `generic` or skip the icon entirely.
 */
export function resolveCategoryForNode(
  provider: 'aws' | 'azure' | 'gcp',
  manifestIdOrType: string | undefined,
): IconCategory | null {
  if (!manifestIdOrType) return null;
  const map = PROVIDER_MAPS[provider];
  const id = manifestIdOrType.toLowerCase();
  // Strip provider prefix: 'aws_rds' → 'rds', 'azure-vm' → 'vm'.
  const stripped = id.replace(/^(aws|azure|gcp)[-_]/, '');
  return map[stripped] ?? map[id] ?? null;
}
